import React, { useEffect, useRef, useState } from 'react';
import './BotCandlestickChart.css'; // Import the CSS file for styling
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  TimeScale,
} from 'chart.js';
import {
  CandlestickController,
  CandlestickElement,
} from 'chartjs-chart-financial';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  CandlestickController,
  CandlestickElement,
  Tooltip,
  Legend
);

const generateOHLC = (prevClose, breakoutChance = 0.1) => {
  let open = prevClose;
  const moveType = Math.random() < 0.5 ? 'gain' : 'loss';

  let change;
  if (moveType === 'gain') {
    change = 0.001 + Math.random() * (0.01 - 0.001);
  } else {
    change = -(0.001 + Math.random() * (0.01 - 0.001));
  }

  let close = open + change;
  close = Math.max(close, 0.1);

  let high = Math.max(open, close) + Math.random() * 0.05;
  let low = Math.min(open, close) - Math.random() * 0.05;
  low = Math.max(low, 0.1);

  if (Math.random() < breakoutChance) {
    const spike = (Math.random() * 0.5 + 0.2) * (Math.random() < 0.5 ? 1 : -1);
    if (spike > 0) {
      high += spike;
    } else {
      low += spike;
      low = Math.max(low, 0.1);
    }
  }

  return { open, high, low, close };
};

const calculateSMA = (data, windowSize) => {
  if (data.length < windowSize) return [];

  const sma = [];
  for (let i = windowSize - 1; i < data.length; i++) {
    const windowSlice = data.slice(i - windowSize + 1, i + 1);
    const sum = windowSlice.reduce((acc, val) => acc + val.close, 0);
    sma.push({ x: data[i].x, y: sum / windowSize });
  }
  return sma;
};

const formatPips = (diff) => (diff * 10000).toFixed(1);

const tokens = ['BTCUSD', 'ETHUSD', 'XRPUSD', 'LTCUSD', 'DOGEUSD'];

const simulationMessages = [
  "Scanning token volatility...",
  "Evaluating market momentum...",
  "Calculating pips from last session...",
  "Analyzing stop-loss impact...",
  "Simulating trade volume spikes..."
];

const BotCandlestickChart = () => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [messages, setMessages] = useState([]);
  const [simulationText, setSimulationText] = useState(simulationMessages[0]);
  const ohlcData = useRef([]);
  const startPrice = 100;

  useEffect(() => {
    const now = Date.now();
    const oneMinute = 60 * 1000;

    let data = [];
    let prevClose = startPrice;

    for (let i = -29; i <= 0; i++) {
      const ohlc = generateOHLC(prevClose, 0.15);
      const candle = {
        x: now + i * oneMinute,
        open: ohlc.open,
        high: ohlc.high,
        low: ohlc.low,
        close: ohlc.close,
      };
      data.push(candle);
      prevClose = ohlc.close;
    }
    ohlcData.current = data;

    const ctx = chartRef.current.getContext('2d');

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    chartInstance.current = new ChartJS(ctx, {
      type: 'candlestick',
      data: {
        datasets: [
          {
            label: 'Price',
            data: ohlcData.current,
            borderColor: '#007bff',
            borderWidth: 1,
            color: {
              up: '#28a745',
              down: '#dc3545',
              unchanged: '#999',
            },
          },
          {
            label: 'SMA (7)',
            type: 'line',
            data: calculateSMA(ohlcData.current, 7),
            borderColor: '#ffa500',
            borderWidth: 2,
            fill: false,
            tension: 0.3,
            pointRadius: 0,
            yAxisID: 'y',
          },
        ],
      },
      options: {
        responsive: true,
        interaction: {
          mode: 'nearest',
          intersect: false,
        },
        plugins: {
          legend: { display: true },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                if (context.dataset.type === 'line') {
                  return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}`;
                }
                const ohlc = context.raw;
                return `O:${ohlc.open.toFixed(2)} H:${ohlc.high.toFixed(2)} L:${ohlc.low.toFixed(2)} C:${ohlc.close.toFixed(2)}`;
              },
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'minute',
              tooltipFormat: 'PPpp',
              displayFormats: {
                minute: 'HH:mm',
              },
            },
            min: ohlcData.current[0].x,
            max: ohlcData.current[ohlcData.current.length - 1].x,
            ticks: {
              source: 'data',
              maxTicksLimit: 20,
              autoSkip: true,
              maxRotation: 0,
              callback: (val) => {
                const date = new Date(val);
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              },
            },
            grid: {
              drawTicks: true,
              drawOnChartArea: true,
              drawBorder: true,
            },
          },
          y: {
            position: 'left',
            title: { display: true, text: 'Price' },
            min: 0,
          },
        },
      },
    });

    const interval = setInterval(() => {
      const lastCandle = ohlcData.current[ohlcData.current.length - 1];
      const newTime = lastCandle.x + oneMinute;
      const newOHLC = generateOHLC(lastCandle.close, 0.15);

      const newCandle = {
        x: newTime,
        open: newOHLC.open,
        high: newOHLC.high,
        low: newOHLC.low,
        close: newOHLC.close,
      };

      if (ohlcData.current.length >= 50) {
        ohlcData.current.shift();
      }
      ohlcData.current.push(newCandle);

      const smaData = calculateSMA(ohlcData.current, 7);

      chartInstance.current.data.datasets[0].data = [...ohlcData.current];
      chartInstance.current.data.datasets[1].data = smaData;
      chartInstance.current.options.scales.x.min = ohlcData.current[0].x;
      chartInstance.current.options.scales.x.max = ohlcData.current[ohlcData.current.length - 1].x;

      chartInstance.current.update();

      const token = tokens[Math.floor(Math.random() * tokens.length)];
      const gainLoss = newOHLC.close - lastCandle.close;
      const pips = formatPips(gainLoss);
      const gainLossStr = gainLoss >= 0 ? '+' : '';

      const message = {
        time: new Date(newTime).toLocaleTimeString(),
        token,
        price: newOHLC.close.toFixed(4),
        gainLoss: `${gainLossStr}${gainLoss.toFixed(4)}`,
        pips: `${gainLossStr}${pips}`,
        direction: gainLoss >= 0 ? 'Gain' : 'Loss',
      };

      setMessages((msgs) => [...msgs.slice(-9), message]);
    }, 3000);

    return () => {
      clearInterval(interval);
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  // Effect to rotate simulation messages every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setSimulationText(
        simulationMessages[Math.floor(Math.random() * simulationMessages.length)]
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ maxWidth: 900, margin: 'auto', fontFamily: 'Arial, sans-serif' }}>
      <p></p>

      {/* Rotating simulation message */}
      <p style={{ fontStyle: 'italic', color: '#555', marginBottom: 10 }}>
      🤖 I'm  {simulationText}
      </p>

      <canvas ref={chartRef} style={{ maxHeight: 200 }} />

      <div style={{ marginTop: 20 }} className="responsive-table-wrapper">
        <h3>Recent Bot Messages (last 10):</h3>
        <table className="bot-table">
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th style={{ padding: 8, border: '1px solid #ddd' }}>Time</th>
              <th style={{ padding: 8, border: '1px solid #ddd' }}>Token</th>
              <th style={{ padding: 8, border: '1px solid #ddd' }}>Price</th>
              <th style={{ padding: 8, border: '1px solid #ddd' }}>Gain/Loss</th>
              <th style={{ padding: 8, border: '1px solid #ddd' }}>Pips</th>
              <th style={{ padding: 8, border: '1px solid #ddd' }}>Direction</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((msg, i) => (
              <tr
                key={i}
                style={{
                  color: msg.direction === 'Gain' ? '#28a745' : '#dc3545',
                  borderBottom: '1px solid #eee',
                }}
              >
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{msg.time}</td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{msg.token}</td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{msg.price}</td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{msg.gainLoss}</td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{msg.pips}</td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{msg.direction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BotCandlestickChart;